// Date-part calculated fields: one part of a date / time column - Day Name, Month Name, Year,
// Week Of Month, Start Of Week, Hour ... - worked out per row (CalculatedField with
// resultType "datePart"; see types.ts).
//
// Like every calculated field, a date-part field becomes a column of its data model (see
// logic/calculatedColumns.ts), so it can be a metric breakdown, an include / exclude list,
// a filter or a categorization condition, and a Full Row Detail column.
//
// SQL rules - mirrored by ReportSqlBuilder.DateParts.cs, keep the two identical:
//   - The column goes through dateSqlForColumn first, so a text-backed date column is
//     converted with the same TRY_CONVERT ladder every other date comparison uses.
//   - Nothing depends on the session's DATEFIRST or LANGUAGE: the day of the week is counted
//     from a known Sunday (1900-01-07) and day / month names are spelled out in a CASE, so the
//     same row gives the same part on every server. Weeks start on Sunday, except the ISO week.
//   - Only constants from this file are written into the SQL text - no user input - so a
//     date-part field never needs a parameter.
//   - A blank date gives a blank part.
import type { CalcDatePartKey, CalculatedField, CalculatedValueType, DataColumn, ModelKey, ReportConfigurationLookupOption } from "../types";
import type { ReportLogicContext } from "./context";
import { allColumnsForModel, columnMatchesKey, isCalculatedColumn } from "./catalogResolution";
import { dateSqlForColumn } from "./sqlExpressions";
import { isTemporalColumn } from "./temporalPrecision";

export type DatePartGroup = "Year & Quarter" | "Month" | "Week" | "Day" | "Time" | "Start / End Of Period";

export interface DatePartDef {
    key: CalcDatePartKey;
    label: string;
    group: DatePartGroup;
    /** What the part evaluates to - drives operators, value pickers and the Excel format. */
    valueType: CalculatedValueType;
    /** Shown under the picker, e.g. "Sunday, Monday, ..." */
    example: string;
    /** False for Hour / AM-PM: the only parts a time-of-day column has. */
    needsDate: boolean;
    /** True for Hour / AM-PM: the part means nothing on a date-only column. */
    needsTime: boolean;
    /** Fixed values offered in include / exclude / condition pickers. */
    options?: { value: string; name: string }[];
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const short = (name: string) => name.slice(0, 3);
const numbered = (from: number, to: number, label?: (value: number) => string) =>
    Array.from({ length: to - from + 1 }, (_, index) => {
        const value = from + index;
        return { value: String(value), name: label ? label(value) : String(value) };
    });
const named = (names: string[]) => names.map((name) => ({ value: name, name }));

export const DATE_PARTS: DatePartDef[] = [
    { key: "year", label: "Year", group: "Year & Quarter", valueType: "number", example: "2026", needsDate: true, needsTime: false },
    { key: "quarter", label: "Quarter Number", group: "Year & Quarter", valueType: "number", example: "1 to 4", needsDate: true, needsTime: false, options: numbered(1, 4) },
    { key: "quarter_label", label: "Quarter", group: "Year & Quarter", valueType: "text", example: "Q1, Q2, Q3, Q4", needsDate: true, needsTime: false, options: named(["Q1", "Q2", "Q3", "Q4"]) },
    { key: "year_quarter", label: "Year And Quarter", group: "Year & Quarter", valueType: "text", example: "2026 Q3", needsDate: true, needsTime: false },
    { key: "month_number", label: "Month Number", group: "Month", valueType: "number", example: "1 to 12", needsDate: true, needsTime: false, options: numbered(1, 12, (value) => `${value} - ${MONTH_NAMES[value - 1]}`) },
    { key: "month_name", label: "Month Name", group: "Month", valueType: "text", example: "January, February, ...", needsDate: true, needsTime: false, options: named(MONTH_NAMES) },
    { key: "month_short", label: "Month Name (Short)", group: "Month", valueType: "text", example: "Jan, Feb, ...", needsDate: true, needsTime: false, options: named(MONTH_NAMES.map(short)) },
    { key: "year_month", label: "Year-Month", group: "Month", valueType: "text", example: "2026-10 (sorts in date order)", needsDate: true, needsTime: false },
    { key: "month_year", label: "Month And Year", group: "Month", valueType: "text", example: "Oct 2026", needsDate: true, needsTime: false },
    { key: "week_of_year", label: "Week Of Year (Sunday Start)", group: "Week", valueType: "number", example: "1 to 54 - week 1 is the week of January 1", needsDate: true, needsTime: false },
    { key: "iso_week", label: "ISO Week Of Year (Monday Start)", group: "Week", valueType: "number", example: "1 to 53 - ISO 8601 weeks", needsDate: true, needsTime: false },
    { key: "week_of_month", label: "Week Of Month (Calendar)", group: "Week", valueType: "number", example: "1 to 6 - the calendar row (Sunday to Saturday) the date falls on; week 1 starts on the 1st", needsDate: true, needsTime: false, options: numbered(1, 6) },
    { key: "week_of_month_7", label: "Week Of Month (Days 1-7, 8-14, ...)", group: "Week", valueType: "number", example: "1 to 5 - days 1-7 are week 1, 8-14 week 2, 29-31 week 5", needsDate: true, needsTime: false, options: numbered(1, 5) },
    { key: "week_start", label: "Start Of Week (Sunday)", group: "Week", valueType: "date", example: "the Sunday on or before the date", needsDate: true, needsTime: false },
    { key: "day_of_month", label: "Day Of Month", group: "Day", valueType: "number", example: "1 to 31", needsDate: true, needsTime: false, options: numbered(1, 31) },
    { key: "day_of_year", label: "Day Of Year", group: "Day", valueType: "number", example: "1 to 366", needsDate: true, needsTime: false },
    { key: "day_of_week", label: "Day Of Week Number (Sunday = 1)", group: "Day", valueType: "number", example: "1 = Sunday ... 7 = Saturday", needsDate: true, needsTime: false, options: numbered(1, 7, (value) => `${value} - ${DAY_NAMES[value - 1]}`) },
    { key: "day_name", label: "Day Name", group: "Day", valueType: "text", example: "Sunday, Monday, ...", needsDate: true, needsTime: false, options: named(DAY_NAMES) },
    { key: "day_short", label: "Day Name (Short)", group: "Day", valueType: "text", example: "Sun, Mon, ...", needsDate: true, needsTime: false, options: named(DAY_NAMES.map(short)) },
    { key: "weekday_weekend", label: "Weekday Or Weekend", group: "Day", valueType: "text", example: "Weekday (Mon-Fri) or Weekend (Sat, Sun)", needsDate: true, needsTime: false, options: named(["Weekday", "Weekend"]) },
    { key: "date_only", label: "Date (Without Time)", group: "Day", valueType: "date", example: "the calendar date", needsDate: true, needsTime: false },
    { key: "hour", label: "Hour Of Day", group: "Time", valueType: "number", example: "0 to 23", needsDate: false, needsTime: true, options: numbered(0, 23, (value) => `${value} (${value % 12 || 12} ${value < 12 ? "AM" : "PM"})`) },
    { key: "am_pm", label: "AM Or PM", group: "Time", valueType: "text", example: "AM (before noon) or PM", needsDate: false, needsTime: true, options: named(["AM", "PM"]) },
    { key: "month_start", label: "Start Of Month", group: "Start / End Of Period", valueType: "date", example: "the 1st of the month", needsDate: true, needsTime: false },
    { key: "month_end", label: "End Of Month", group: "Start / End Of Period", valueType: "date", example: "the last day of the month", needsDate: true, needsTime: false },
    { key: "quarter_start", label: "Start Of Quarter", group: "Start / End Of Period", valueType: "date", example: "Jan 1, Apr 1, Jul 1 or Oct 1", needsDate: true, needsTime: false },
    { key: "year_start", label: "Start Of Year", group: "Start / End Of Period", valueType: "date", example: "January 1", needsDate: true, needsTime: false },
];

export function datePartDef(key: string | undefined | null) {
    return DATE_PARTS.find((part) => part.key === key) || null;
}

export function isDatePartCalculatedField(field: CalculatedField | null | undefined) {
    return field?.resultType === "datePart";
}

// --- Columns --------------------------------------------------------------------------

function sqlType(column?: DataColumn | null) {
    return String(column?.dataType || "").trim().toLowerCase().replace(/\(.*$/, "");
}

/** A `time` column holds a time of day only - no date parts. */
export function isTimeOfDayColumn(column?: DataColumn | null) {
    return sqlType(column) === "time";
}

/** A `date` column has no time - no hour / AM-PM. */
export function isDateOnlyColumn(column?: DataColumn | null) {
    return sqlType(column) === "date";
}

/** Date / time columns a date-part field may read: temporal, not sensitive, not another calculated field. Mirrors IsCalcDateColumn. */
export function datePartColumns(context: ReportLogicContext, modelKey: ModelKey | "") {
    return modelKey
        ? allColumnsForModel(context, modelKey).filter((column) => !isCalculatedColumn(column) && column.isSensitive !== true && isTemporalColumn(column))
        : [];
}

export function datePartColumn(context: ReportLogicContext, field: CalculatedField) {
    const key = field.dateColumn;
    return key ? datePartColumns(context, field.model).find((column) => columnMatchesKey(column, key)) || null : null;
}

/** Whether `part` can be taken from `column` (a time-of-day column has no date; a date column has no time). */
export function datePartFitsColumn(part: DatePartDef | null, column: DataColumn | null) {
    if (!part || !column) return false;
    if (part.needsDate && isTimeOfDayColumn(column)) return false;
    if (part.needsTime && isDateOnlyColumn(column)) return false;
    return true;
}

/** The parts a column offers, in catalog order. */
export function datePartsForColumn(column: DataColumn | null) {
    return column ? DATE_PARTS.filter((part) => datePartFitsColumn(part, column)) : DATE_PARTS;
}

// --- SQL ------------------------------------------------------------------------------

// Day 0 of the week count: 1900-01-07 was a Sunday. ((n % 7) + 7) % 7 keeps dates before it
// in range, so the result is 0 = Sunday ... 6 = Saturday whatever DATEFIRST is set to.
const SUNDAY_ANCHOR = "CONVERT(DATE, '19000107', 112)";
const weekday0 = (d: string) => `((DATEDIFF(DAY, ${SUNDAY_ANCHOR}, ${d}) % 7 + 7) % 7)`;
const dayOfWeek = (d: string) => `(${weekday0(d)} + 1)`;
const nameCase = (index: string, names: string[]) =>
    `CASE ${index} ${names.map((name, position) => `WHEN ${position + 1} THEN N'${name}'`).join(" ")} END`;
const firstOfMonth = (d: string) => `DATEADD(DAY, 1 - DAY(${d}), CAST(${d} AS DATE))`;
const firstOfYear = (d: string) => `DATEFROMPARTS(YEAR(${d}), 1, 1)`;
const text = (value: string, length: number) => `CONVERT(NVARCHAR(${length}), ${value})`;

/** The SQL for one part of the (already converted) date expression `d`. Mirrors DatePartSql. */
export function datePartSql(part: CalcDatePartKey, d: string): string | null {
    switch (part) {
        case "year": return `YEAR(${d})`;
        case "quarter": return `DATEPART(QUARTER, ${d})`;
        case "quarter_label": return `N'Q' + ${text(`DATEPART(QUARTER, ${d})`, 1)}`;
        case "year_quarter": return `${text(`YEAR(${d})`, 4)} + N' Q' + ${text(`DATEPART(QUARTER, ${d})`, 1)}`;
        case "month_number": return `MONTH(${d})`;
        case "month_name": return nameCase(`MONTH(${d})`, MONTH_NAMES);
        case "month_short": return nameCase(`MONTH(${d})`, MONTH_NAMES.map(short));
        case "year_month": return `CONVERT(NVARCHAR(7), CAST(${d} AS DATE), 23)`;
        case "month_year": return `${nameCase(`MONTH(${d})`, MONTH_NAMES.map(short))} + N' ' + ${text(`YEAR(${d})`, 4)}`;
        // Week 1 holds January 1; weeks run Sunday to Saturday (DATEPART(WEEK) under DATEFIRST 7).
        case "week_of_year": return `((DATEPART(DAYOFYEAR, ${d}) + ${weekday0(firstOfYear(d))} - 1) / 7 + 1)`;
        case "iso_week": return `DATEPART(ISO_WEEK, ${d})`;
        // The calendar row of the month the date sits on, rows running Sunday to Saturday.
        case "week_of_month": return `((DAY(${d}) + ${weekday0(firstOfMonth(d))} - 1) / 7 + 1)`;
        case "week_of_month_7": return `((DAY(${d}) - 1) / 7 + 1)`;
        case "week_start": return `DATEADD(DAY, -${weekday0(d)}, CAST(${d} AS DATE))`;
        case "day_of_month": return `DAY(${d})`;
        case "day_of_year": return `DATEPART(DAYOFYEAR, ${d})`;
        case "day_of_week": return dayOfWeek(d);
        case "day_name": return nameCase(dayOfWeek(d), DAY_NAMES);
        case "day_short": return nameCase(dayOfWeek(d), DAY_NAMES.map(short));
        case "weekday_weekend": return `CASE WHEN ${d} IS NULL THEN NULL WHEN ${weekday0(d)} IN (0, 6) THEN N'Weekend' ELSE N'Weekday' END`;
        case "date_only": return `CAST(${d} AS DATE)`;
        case "hour": return `DATEPART(HOUR, ${d})`;
        case "am_pm": return `CASE WHEN ${d} IS NULL THEN NULL WHEN DATEPART(HOUR, ${d}) < 12 THEN N'AM' ELSE N'PM' END`;
        case "month_start": return firstOfMonth(d);
        case "month_end": return `EOMONTH(${d})`;
        case "quarter_start": return `DATEFROMPARTS(YEAR(${d}), (DATEPART(QUARTER, ${d}) - 1) * 3 + 1, 1)`;
        case "year_start": return firstOfYear(d);
        default: return null;
    }
}

/** The field's SQL, or null when its column or part is missing or they don't fit. */
export function datePartFieldSql(context: ReportLogicContext, field: CalculatedField): string | null {
    const part = datePartDef(field.datePart);
    const column = datePartColumn(context, field);
    if (!part || !column || !datePartFitsColumn(part, column)) return null;
    return datePartSql(part.key, dateSqlForColumn(column));
}

export function datePartValueType(field: CalculatedField): CalculatedValueType {
    return datePartDef(field.datePart)?.valueType || "text";
}

// --- Plain English / validation -------------------------------------------------------

/** e.g. "Day Name of Pickup Date". */
export function datePartFieldEnglish(context: ReportLogicContext, field: CalculatedField) {
    const part = datePartDef(field.datePart);
    const column = datePartColumn(context, field);
    if (!part && !column) return "";
    return `${part?.label || "?"} of ${column?.label || "?"}`;
}

export function datePartFieldIssues(context: ReportLogicContext, field: CalculatedField): string[] {
    const issues: string[] = [];
    if (!field.model) return issues;
    const column = datePartColumn(context, field);
    const part = datePartDef(field.datePart);
    if (!field.dateColumn) issues.push("Choose the date column to take the part from.");
    else if (!column) issues.push("That column is not a usable date / time column on this data model.");
    if (!field.datePart) issues.push("Choose which part of the date to use.");
    else if (!part) issues.push("Choose a date part from the list.");
    if (part && column && !datePartFitsColumn(part, column)) {
        issues.push(isTimeOfDayColumn(column)
            ? `${column.label} holds a time of day only - choose Hour Of Day or AM Or PM, or another column.`
            : `${column.label} holds dates without a time - "${part.label}" needs a column with a time.`);
    }
    return issues;
}

/** Fixed values for value pickers (Monday ... Sunday, Q1 ... Q4); empty when the part has none. */
export function datePartValueOptions(field: CalculatedField): ReportConfigurationLookupOption[] {
    return (datePartDef(field.datePart)?.options || []).map((option, index) => ({ id: option.value, value: option.value, name: option.name, sortOrder: index }));
}

// --- "Try it" -------------------------------------------------------------------------

const pad = (value: number) => String(value).padStart(2, "0");

function parseSample(value: string): Date | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    // A bare time of day (time columns): put it on an arbitrary date.
    const time = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
    if (time) return new Date(Date.UTC(2000, 0, 1, Number(time[1]), Number(time[2]), Number(time[3] || 0)));
    const match = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(trimmed);
    if (!match) return null;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4] || 0), Number(match[5] || 0), Number(match[6] || 0)));
    return Number.isNaN(date.getTime()) || date.getUTCDate() !== Number(match[3]) ? null : date;
}

const isoDate = (date: Date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
const addDays = (date: Date, days: number) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));

/** The part of a sample value, worked out the way the SQL does. "(blank)" for no / bad date. */
export function simulateDatePart(part: CalcDatePartKey | undefined, sample: string): string {
    const date = parseSample(sample);
    if (!date || !part) return "(blank)";
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const weekday = date.getUTCDay(); // 0 = Sunday, as weekday0 in the SQL
    const quarter = Math.floor((month - 1) / 3) + 1;
    const startOfYear = new Date(Date.UTC(year, 0, 1));
    const dayOfYear = Math.round((Date.UTC(year, month - 1, day) - startOfYear.getTime()) / 86400000) + 1;
    switch (part) {
        case "year": return String(year);
        case "quarter": return String(quarter);
        case "quarter_label": return `Q${quarter}`;
        case "year_quarter": return `${year} Q${quarter}`;
        case "month_number": return String(month);
        case "month_name": return MONTH_NAMES[month - 1];
        case "month_short": return short(MONTH_NAMES[month - 1]);
        case "year_month": return `${year}-${pad(month)}`;
        case "month_year": return `${short(MONTH_NAMES[month - 1])} ${year}`;
        case "week_of_year": return String(Math.floor((dayOfYear + startOfYear.getUTCDay() - 1) / 7) + 1);
        case "iso_week": {
            const thursday = addDays(date, 3 - ((weekday + 6) % 7));
            const isoYearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
            return String(Math.floor((thursday.getTime() - isoYearStart.getTime()) / 86400000 / 7) + 1);
        }
        case "week_of_month": return String(Math.floor((day + new Date(Date.UTC(year, month - 1, 1)).getUTCDay() - 1) / 7) + 1);
        case "week_of_month_7": return String(Math.floor((day - 1) / 7) + 1);
        case "week_start": return isoDate(addDays(date, -weekday));
        case "day_of_month": return String(day);
        case "day_of_year": return String(dayOfYear);
        case "day_of_week": return String(weekday + 1);
        case "day_name": return DAY_NAMES[weekday];
        case "day_short": return short(DAY_NAMES[weekday]);
        case "weekday_weekend": return weekday === 0 || weekday === 6 ? "Weekend" : "Weekday";
        case "date_only": return isoDate(date);
        case "hour": return String(date.getUTCHours());
        case "am_pm": return date.getUTCHours() < 12 ? "AM" : "PM";
        case "month_start": return isoDate(new Date(Date.UTC(year, month - 1, 1)));
        case "month_end": return isoDate(new Date(Date.UTC(year, month, 0)));
        case "quarter_start": return isoDate(new Date(Date.UTC(year, (quarter - 1) * 3, 1)));
        case "year_start": return isoDate(startOfYear);
        default: return "(blank)";
    }
}

export function simulateDatePartField(context: ReportLogicContext, field: CalculatedField, inputs: Record<string, string>) {
    const column = datePartColumn(context, field);
    return column ? simulateDatePart(field.datePart, String(inputs[column.key] ?? "")) : "(blank)";
}

export function datePartInputColumns(context: ReportLogicContext, field: CalculatedField): DataColumn[] {
    const column = datePartColumn(context, field);
    return column ? [column] : [];
}
